import { useTranslation } from 'react-i18next';
import { makeDefaultSections } from '../config/quoteSectionDefaults';
import type {
  AppSettings,
  QuoteSectionScopeRow,
  QuoteSectionTechnicalRow,
  QuoteSectionTermRow,
  QuoteSections,
} from '../types';

interface QuoteSectionsPanelProps {
  sections: QuoteSections;
  onChange: (sections: QuoteSections) => void;
  settings: AppSettings;
}

type SectionKey = keyof QuoteSections;

const emptyTechnicalRow = (): QuoteSectionTechnicalRow => ({
  pos: '',
  code: '',
  description: '',
  qty: '',
});

const emptyScopeRow = (): QuoteSectionScopeRow => ({
  party: '',
  content: '',
});

const emptyTermRow = (): QuoteSectionTermRow => ({
  param: '',
  value: '',
});

export default function QuoteSectionsPanel({ sections, onChange, settings }: QuoteSectionsPanelProps) {
  const { t, i18n } = useTranslation();

  const defaults = () => makeDefaultSections(settings, i18n.getFixedT(settings.printLanguage));

  const updateSections = (next: QuoteSections) => {
    onChange(next);
  };

  const toggleSection = (key: SectionKey) => {
    const existing = sections[key];
    if (existing?.enabled) {
      updateSections({
        ...sections,
        [key]: { ...existing, enabled: false },
      });
      return;
    }

    const defaultSection = defaults()[key];
    let nextSection = existing || defaultSection;

    if (key === 'technical') {
      const current = sections.technical;
      nextSection = current?.rows?.length ? current : defaultSection;
    } else if (key === 'scope') {
      const current = sections.scope;
      nextSection = current?.rows?.length ? current : defaultSection;
    } else if (key === 'terms') {
      const current = sections.terms;
      nextSection = current?.rows?.length ? current : defaultSection;
    } else if (key === 'attachments') {
      const current = sections.attachments;
      nextSection = current?.items?.length ? current : defaultSection;
    } else if (key === 'signature') {
      const current = sections.signature;
      const hasContent = Boolean(current && (current.name || current.title || current.email || current.phone));
      nextSection = hasContent ? current : defaultSection;
    }

    updateSections({
      ...sections,
      [key]: { ...nextSection, enabled: true },
    });
  };

  const updateTechnicalRow = (index: number, field: keyof QuoteSectionTechnicalRow, value: string) => {
    const current = sections.technical || { enabled: true, rows: [] };
    updateSections({
      ...sections,
      technical: {
        ...current,
        rows: current.rows.map((row, rowIndex) => rowIndex === index ? { ...row, [field]: value } : row),
      },
    });
  };

  const addTechnicalRow = () => {
    const current = sections.technical || { enabled: true, rows: [] };
    updateSections({
      ...sections,
      technical: { ...current, rows: [...current.rows, emptyTechnicalRow()] },
    });
  };

  const removeTechnicalRow = (index: number) => {
    const current = sections.technical || { enabled: true, rows: [] };
    updateSections({
      ...sections,
      technical: { ...current, rows: current.rows.filter((_, rowIndex) => rowIndex !== index) },
    });
  };

  const updateScopeRow = (index: number, field: keyof QuoteSectionScopeRow, value: string) => {
    const current = sections.scope || { enabled: true, rows: [] };
    updateSections({
      ...sections,
      scope: {
        ...current,
        rows: current.rows.map((row, rowIndex) => rowIndex === index ? { ...row, [field]: value } : row),
      },
    });
  };

  const addScopeRow = () => {
    const current = sections.scope || { enabled: true, rows: [] };
    updateSections({
      ...sections,
      scope: { ...current, rows: [...current.rows, emptyScopeRow()] },
    });
  };

  const removeScopeRow = (index: number) => {
    const current = sections.scope || { enabled: true, rows: [] };
    updateSections({
      ...sections,
      scope: { ...current, rows: current.rows.filter((_, rowIndex) => rowIndex !== index) },
    });
  };

  const updateTermRow = (index: number, field: keyof QuoteSectionTermRow, value: string) => {
    const current = sections.terms || { enabled: true, rows: [] };
    updateSections({
      ...sections,
      terms: {
        ...current,
        rows: current.rows.map((row, rowIndex) => rowIndex === index ? { ...row, [field]: value } : row),
      },
    });
  };

  const addTermRow = () => {
    const current = sections.terms || { enabled: true, rows: [] };
    updateSections({
      ...sections,
      terms: { ...current, rows: [...current.rows, emptyTermRow()] },
    });
  };

  const removeTermRow = (index: number) => {
    const current = sections.terms || { enabled: true, rows: [] };
    updateSections({
      ...sections,
      terms: { ...current, rows: current.rows.filter((_, rowIndex) => rowIndex !== index) },
    });
  };

  const updateAttachment = (index: number, value: string) => {
    const current = sections.attachments || { enabled: true, items: [] };
    updateSections({
      ...sections,
      attachments: {
        ...current,
        items: current.items.map((item, itemIndex) => itemIndex === index ? value : item),
      },
    });
  };

  const addAttachment = () => {
    const current = sections.attachments || { enabled: true, items: [] };
    updateSections({
      ...sections,
      attachments: { ...current, items: [...current.items, ''] },
    });
  };

  const removeAttachment = (index: number) => {
    const current = sections.attachments || { enabled: true, items: [] };
    updateSections({
      ...sections,
      attachments: { ...current, items: current.items.filter((_, itemIndex) => itemIndex !== index) },
    });
  };

  const updateSignature = (field: 'name' | 'title' | 'email' | 'phone', value: string) => {
    const current = sections.signature || {
      enabled: true,
      name: '',
      title: '',
      email: '',
      phone: '',
    };
    updateSections({
      ...sections,
      signature: { ...current, [field]: value },
    });
  };

  const renderToggle = (key: SectionKey, label: string) => (
    <div className="checkbox-group">
      <label className="checkbox-label">
        <input
          type="checkbox"
          data-testid={`qsec-toggle-${key}`}
          checked={Boolean(sections[key]?.enabled)}
          onChange={() => toggleSection(key)}
        />
        <span>{label}</span>
      </label>
    </div>
  );

  return (
    <>
      <div className="item-card">
        {renderToggle('technical', t('calculator.sections.technical'))}
        {sections.technical?.enabled && (
          <div className="form-group">
            {sections.technical.rows.map((row, index) => (
              <div className="item-card" key={index}>
                <div className="form-row form-row-4">
                  <div className="form-group">
                    <label>{t('calculator.sections.techPos')}</label>
                    <input data-testid={`qsec-technical-pos-${index}`} className="form-input" value={row.pos} onChange={(e) => updateTechnicalRow(index, 'pos', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>{t('calculator.sections.techCode')}</label>
                    <input data-testid={`qsec-technical-code-${index}`} className="form-input" value={row.code} onChange={(e) => updateTechnicalRow(index, 'code', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>{t('calculator.sections.techDescription')}</label>
                    <input data-testid={`qsec-technical-description-${index}`} className="form-input" value={row.description} onChange={(e) => updateTechnicalRow(index, 'description', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>{t('calculator.sections.techQty')}</label>
                    <input data-testid={`qsec-technical-qty-${index}`} className="form-input" value={row.qty} onChange={(e) => updateTechnicalRow(index, 'qty', e.target.value)} />
                  </div>
                </div>
                <button type="button" className="btn btn-secondary btn-sm" data-testid={`qsec-technical-remove-${index}`} onClick={() => removeTechnicalRow(index)}>
                  {t('calculator.sections.remove')}
                </button>
              </div>
            ))}
            <button type="button" className="btn btn-secondary btn-sm" data-testid="qsec-technical-add" onClick={addTechnicalRow}>
              {t('calculator.sections.addRow')}
            </button>
          </div>
        )}
      </div>

      <div className="item-card">
        {renderToggle('scope', t('calculator.sections.scope'))}
        {sections.scope?.enabled && (
          <div className="form-group">
            {sections.scope.rows.map((row, index) => (
              <div className="form-row form-row-2" key={index}>
                <div className="form-group">
                  <label>{t('calculator.sections.scopeParty')}</label>
                  <input data-testid={`qsec-scope-party-${index}`} className="form-input" value={row.party} onChange={(e) => updateScopeRow(index, 'party', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>{t('calculator.sections.scopeContent')}</label>
                  <input data-testid={`qsec-scope-content-${index}`} className="form-input" value={row.content} onChange={(e) => updateScopeRow(index, 'content', e.target.value)} />
                </div>
                <button type="button" className="btn btn-secondary btn-sm" data-testid={`qsec-scope-remove-${index}`} onClick={() => removeScopeRow(index)}>
                  {t('calculator.sections.remove')}
                </button>
              </div>
            ))}
            <button type="button" className="btn btn-secondary btn-sm" data-testid="qsec-scope-add" onClick={addScopeRow}>
              {t('calculator.sections.addRow')}
            </button>
          </div>
        )}
      </div>

      <div className="item-card">
        {renderToggle('terms', t('calculator.sections.terms'))}
        {sections.terms?.enabled && (
          <div className="form-group">
            {sections.terms.rows.map((row, index) => (
              <div className="form-row form-row-2" key={index}>
                <div className="form-group">
                  <label>{t('calculator.sections.termParam')}</label>
                  <input data-testid={`qsec-terms-param-${index}`} className="form-input" value={row.param} onChange={(e) => updateTermRow(index, 'param', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>{t('calculator.sections.termValue')}</label>
                  <input data-testid={`qsec-terms-value-${index}`} className="form-input" value={row.value} onChange={(e) => updateTermRow(index, 'value', e.target.value)} />
                </div>
                <button type="button" className="btn btn-secondary btn-sm" data-testid={`qsec-terms-remove-${index}`} onClick={() => removeTermRow(index)}>
                  {t('calculator.sections.remove')}
                </button>
              </div>
            ))}
            <button type="button" className="btn btn-secondary btn-sm" data-testid="qsec-terms-add" onClick={addTermRow}>
              {t('calculator.sections.addRow')}
            </button>
          </div>
        )}
      </div>

      <div className="item-card">
        {renderToggle('attachments', t('calculator.sections.attachments'))}
        {sections.attachments?.enabled && (
          <div className="form-group">
            {sections.attachments.items.map((item, index) => (
              <div className="form-row form-row-2" key={index}>
                <div className="form-group">
                  <label>{t('calculator.sections.attachmentItem')}</label>
                  <input data-testid={`qsec-attachments-item-${index}`} className="form-input" value={item} onChange={(e) => updateAttachment(index, e.target.value)} />
                </div>
                <button type="button" className="btn btn-secondary btn-sm" data-testid={`qsec-attachments-remove-${index}`} onClick={() => removeAttachment(index)}>
                  {t('calculator.sections.remove')}
                </button>
              </div>
            ))}
            <button type="button" className="btn btn-secondary btn-sm" data-testid="qsec-attachments-add" onClick={addAttachment}>
              {t('calculator.sections.addRow')}
            </button>
          </div>
        )}
      </div>

      <div className="item-card">
        {renderToggle('signature', t('calculator.sections.signature'))}
        {sections.signature?.enabled && (
          <div className="form-row form-row-2">
            <div className="form-group">
              <label>{t('calculator.sections.signatureName')}</label>
              <input data-testid="qsec-signature-name" className="form-input" value={sections.signature.name} onChange={(e) => updateSignature('name', e.target.value)} />
            </div>
            <div className="form-group">
              <label>{t('calculator.sections.signatureTitle')}</label>
              <input data-testid="qsec-signature-title" className="form-input" value={sections.signature.title} onChange={(e) => updateSignature('title', e.target.value)} />
            </div>
            <div className="form-group">
              <label>{t('calculator.sections.signatureEmail')}</label>
              <input data-testid="qsec-signature-email" className="form-input" value={sections.signature.email} onChange={(e) => updateSignature('email', e.target.value)} />
            </div>
            <div className="form-group">
              <label>{t('calculator.sections.signaturePhone')}</label>
              <input data-testid="qsec-signature-phone" className="form-input" value={sections.signature.phone} onChange={(e) => updateSignature('phone', e.target.value)} />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
